import type { WorkflowDiagramToolHandlers } from './useWorkflowDiagramToolHandlers.types';
import type { WorkflowDiagramToolData } from './useWorkflowDiagramToolData.types';
import type { WorkflowDiagramToolProps } from '../WorkflowDiagramTool.types';

import * as helpers from '../WorkflowDiagramTool.helpers';
import * as constants from '../WorkflowDiagramTool.constants';

import { getNodesBounds, getViewportForBounds, useReactFlow } from 'reactflow';
import { toPng } from 'html-to-image';
import { logError } from 'utils/errors';

import { useCallback } from 'react';

export const useWorkflowDiagramToolHandlers = ({
  props,
  localActions
}: {
  props: Pick<WorkflowDiagramToolProps, 'workflow'>;
  localActions: WorkflowDiagramToolData['localActions'];
}): WorkflowDiagramToolHandlers => {
  const { getNodes } = useReactFlow();

  const downloadImage = (dataUrl: string) => {
    const a = document.createElement('a');

    a.setAttribute('download', `${props.workflow?.name || 'diagram'}.png`);
    a.setAttribute('href', dataUrl);
    a.click();
  };

  const handleDiagramPrint: WorkflowDiagramToolHandlers['handleDiagramPrint'] = async () => {
    const nodesBounds = getNodesBounds(getNodes());

    const reactFlowElement = document.querySelector(constants.DIAGRAM_SELECTOR) as HTMLElement;

    if (!reactFlowElement) {
      logError(new Error('Unable to export the diagram'));
      return;
    }

    const width = reactFlowElement.scrollWidth;
    const height = reactFlowElement.scrollHeight;

    const viewport = getViewportForBounds(nodesBounds, width, height, 0.5, 2);

    const image = await toPng(reactFlowElement, {
      backgroundColor: 'white',
      width,
      height,
      style: {
        width: String(width),
        height: String(height),
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`
      }
    });

    downloadImage(image);
  };

  const handleInitialNodesSet: WorkflowDiagramToolHandlers['handleInitialNodesSet'] =
    useCallback(() => {
      if (!props.workflow) {
        localActions.setNodes([]);
        localActions.setEdges([]);
      } else {
        const { nodes, edges } = helpers.getLayoutedElements([
          ...helpers.formatTasksForDiagram(props.workflow),
          ...helpers.formatTasksLinksForDiagram(props.workflow.tasks)
        ]);

        localActions.setEdges(edges);
        localActions.setNodes(nodes);
      }
    }, [localActions, props.workflow]);

  const handleDiagramLoad: WorkflowDiagramToolHandlers['handleDiagramLoad'] = useCallback(
    reactFlowInstance => {
      localActions.setDiagramInstance(reactFlowInstance);
    },
    [localActions]
  );

  return {
    handleInitialNodesSet,
    handleDiagramLoad,
    handleDiagramPrint
  };
};
